import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Scanner } from "@yudiel/react-qr-scanner";
import NewsletterTab from "@/components/NewsletterTab";
import {
  House,
  Ticket,
  QrCode,
  Users,
  Gift,
  Plus,
  Pencil,
  Trash,
  Check,
  X,
  CaretLeft,
  Camera,
  Coffee,
  CookingPot,
  ChartBar,
  SignOut,
  Bell,
  PaperPlaneTilt,
  Image as ImageIcon,
  Link as LinkIcon,
  CalendarBlank,
  Clock,
  Phone,
  User,
  MapPin,
  Truck,
  Gear,
  Wallet,
  Star,
  Keyboard
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════
// COLORS
// ═══════════════════════════════════════════════════════════
const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  orange: "#E07A3D",
  tan: "#D4A574",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
  red: "#E74C3C",
};

// Was Membership-Kunden bekommen / nicht bekommen — Mitarbeiter-Hilfe am Scanner
const PLAN_DETAILS = {
  coffee: {
    label: "Coffee Pass",
    frequency: "1× pro Tag",
    resetText: "Reset um Mitternacht",
    includes: ["Espresso", "Cappuccino", "Latte Macchiato", "Filterkaffee", "Americano"],
    excludes: ["Matcha (Aufpreis)", "Tee", "Säfte / Sodas", "Speisen / Cheesecake / Pasta"],
  },
  matcha: {
    label: "Matcha Lover",
    frequency: "1× pro Woche",
    resetText: "Reset jeden Montag",
    includes: ["Ice Matcha Latte (1× pro Woche gratis)", "20 % Rabatt auf alle anderen Getränke"],
    excludes: ["Speisen / Cheesecake / Pasta (kein Rabatt)"],
  },
};

// ═══════════════════════════════════════════════════════════
// TAB NAVIGATION - Role-based
// ═══════════════════════════════════════════════════════════
const ADMIN_TABS = [
  { id: "dashboard", label: "Dashboard", icon: ChartBar },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "scanner", label: "Scanner", icon: QrCode },
  { id: "reservations", label: "Tische", icon: CalendarBlank },
  { id: "members", label: "Members", icon: Users },
  { id: "rewards", label: "Rewards", icon: Gift },
  { id: "push", label: "Push", icon: Bell },
  { id: "delivery", label: "Lieferung", icon: Truck },
  { id: "newsletter", label: "Newsletter", icon: PaperPlaneTilt },
  { id: "settings", label: "Settings", icon: Gear },
];

// Staff kann nur: Scanner (Coupons/Stempel/Einlösungen), Tische
const STAFF_TABS = [
  { id: "scanner", label: "Kasse", icon: QrCode },
  { id: "reservations", label: "Tische", icon: CalendarBlank },
];

const TabBar = ({ active, onChange, isAdmin }) => {
  const tabs = isAdmin ? ADMIN_TABS : STAFF_TABS;
  
  return (
    <div className="flex gap-1 p-1 rounded-2xl mb-6 overflow-x-auto" style={{ backgroundColor: COLORS.white }}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = active === tab.id;
        return (
          <motion.button
            key={tab.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => onChange(tab.id)}
            className="flex-1 py-3 px-2 rounded-xl flex flex-col items-center gap-1 text-xs font-bold min-w-[70px]"
            style={{
              backgroundColor: isActive ? COLORS.green : "transparent",
              color: isActive ? COLORS.white : COLORS.textMuted,
            }}
          >
            <Icon size={20} weight={isActive ? "fill" : "regular"} />
            <span>{tab.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// BACKOFFICE LOGIN
// ═══════════════════════════════════════════════════════════
const BackofficeLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      // Direct API call to avoid auth context issues
      const res = await fetch((API || "") + "/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Login fehlgeschlagen");
        setLoading(false);
        return;
      }
      if (!["staff", "admin"].includes(data.user?.role)) {
        setError("Nur für Mitarbeiter zugänglich");
        setLoading(false);
        return;
      }
      localStorage.setItem("token", data.access_token);
      // Also update auth context if available
      try { await login(email, password); } catch(e) {}
      window.location.reload();
    } catch (err) {
      setError("Verbindung fehlgeschlagen: " + (err.message || "Unbekannt"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: COLORS.bg }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-6 rounded-3xl"
        style={{ backgroundColor: COLORS.white }}
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: COLORS.green }}>
            <QrCode size={32} weight="fill" className="text-white" />
          </div>
          <h1 className="text-2xl font-black" style={{ color: COLORS.text }}>
            Mitarbeiter-Login
          </h1>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            Backoffice & Kasse
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 rounded-xl mb-4 text-center" style={{ backgroundColor: COLORS.red + "15" }}>
            <p className="text-sm font-bold" style={{ color: COLORS.red }}>{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: COLORS.text }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mitarbeiter@frankysfarm.de"
              className="w-full p-3 rounded-xl outline-none"
              style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
              required
            />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: COLORS.text }}>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full p-3 rounded-xl outline-none"
              style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
              required
            />
          </div>
          <motion.button
            type="submit"
            whileTap={{ scale: 0.95 }}
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: COLORS.green }}
          >
            {loading ? "Anmelden..." : "Anmelden"}
          </motion.button>
        </form>

        {/* Help text */}
        <p className="text-center text-xs mt-4" style={{ color: COLORS.textMuted }}>
          Probleme beim Login? Kontaktiere deinen Admin.
        </p>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════════════════════
const DashboardTab = ({ stats, location }) => (
  <div className="space-y-4">
    <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
      Dashboard - {location.charAt(0).toUpperCase() + location.slice(1)}
    </h2>
    
    <div className="grid grid-cols-2 gap-3">
      <StatCard 
        icon="👥" 
        label="Members" 
        value={stats.total_members || 0}
        color={COLORS.green}
      />
      <StatCard 
        icon="⭐" 
        label="Abos aktiv" 
        value={stats.active_memberships || 0}
        color={COLORS.orange}
      />
      <StatCard 
        icon="🎫" 
        label="Aktive Coupons" 
        value={stats.active_coupons || 0}
        color={COLORS.tan}
      />
      <StatCard 
        icon="✅" 
        label="Heute eingelöst" 
        value={stats.coupons_redeemed_today || 0}
        color={COLORS.green}
      />
      <StatCard 
        icon="☕" 
        label="Stempel heute" 
        value={stats.stamps_given_today || 0}
        color={COLORS.orange}
        fullWidth
      />
    </div>
  </div>
);

const StatCard = ({ icon, label, value, color, fullWidth }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`p-4 rounded-2xl ${fullWidth ? "col-span-2" : ""}`}
    style={{ backgroundColor: COLORS.white }}
  >
    <div className="flex items-center gap-3">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
        style={{ backgroundColor: color + "20" }}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-black" style={{ color: COLORS.text }}>{value}</p>
        <p className="text-sm" style={{ color: COLORS.textMuted }}>{label}</p>
      </div>
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
// COUPONS TAB
// ═══════════════════════════════════════════════════════════
const CouponsTab = ({ coupons, onRefresh, token }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    value: "",
    valid_days: 7,
    badge_type: "",
    is_welcome_gift: false
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      value: "",
      valid_days: 7,
      badge_type: "",
      is_welcome_gift: false
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCoupon) {
        await axios.put(
          `${API}/api/backoffice/coupons/${editingCoupon.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Coupon aktualisiert!");
      } else {
        await axios.post(
          `${API}/api/backoffice/coupons`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Coupon erstellt!");
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Coupon wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/api/backoffice/coupons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coupon gelöscht!");
      onRefresh();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(
        `${API}/api/backoffice/coupons/${coupon.id}`,
        { active: !coupon.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(coupon.active ? "Coupon deaktiviert" : "Coupon aktiviert");
      onRefresh();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
          Coupons verwalten
        </h2>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowForm(true)}
          className="px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2"
          style={{ backgroundColor: COLORS.green, color: COLORS.white }}
        >
          <Plus size={18} weight="bold" />
          Neu
        </motion.button>
      </div>

      {/* Coupon Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => resetForm()}
          >
            <motion.form
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleSubmit}
              className="w-full max-w-md p-6 rounded-3xl space-y-4"
              style={{ backgroundColor: COLORS.white }}
            >
              <h3 className="text-lg font-black" style={{ color: COLORS.text }}>
                {editingCoupon ? "Coupon bearbeiten" : "Neuer Coupon"}
              </h3>
              
              <input
                type="text"
                placeholder="Titel (z.B. 20% Rabatt)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full p-3 rounded-xl border-2"
                style={{ borderColor: COLORS.bg }}
              />
              
              <input
                type="text"
                placeholder="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="w-full p-3 rounded-xl border-2"
                style={{ borderColor: COLORS.bg }}
              />
              
              <input
                type="text"
                placeholder="Wert (z.B. -20% oder Gratis Kaffee)"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                required
                className="w-full p-3 rounded-xl border-2"
                style={{ borderColor: COLORS.bg }}
              />
              
              <div className="flex gap-3">
                <input
                  type="number"
                  placeholder="Gültig (Tage)"
                  value={formData.valid_days}
                  onChange={(e) => setFormData({ ...formData, valid_days: parseInt(e.target.value) })}
                  className="flex-1 p-3 rounded-xl border-2"
                  style={{ borderColor: COLORS.bg }}
                />
                <select
                  value={formData.badge_type}
                  onChange={(e) => setFormData({ ...formData, badge_type: e.target.value })}
                  className="flex-1 p-3 rounded-xl border-2"
                  style={{ borderColor: COLORS.bg }}
                >
                  <option value="">Kein Badge</option>
                  <option value="NEU">NEU</option>
                  <option value="HOT">HOT 🔥</option>
                  <option value="LIMITED">LIMITED</option>
                </select>
              </div>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_welcome_gift}
                  onChange={(e) => setFormData({ ...formData, is_welcome_gift: e.target.checked })}
                  className="w-5 h-5"
                  style={{ accentColor: COLORS.green }}
                />
                <span style={{ color: COLORS.textMuted }}>Als Willkommens-Coupon</span>
              </label>
              
              <div className="flex gap-3 pt-2">
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.95 }}
                  onClick={resetForm}
                  className="flex-1 py-3 rounded-xl font-bold"
                  style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
                >
                  Abbrechen
                </motion.button>
                <motion.button
                  type="submit"
                  whileTap={{ scale: 0.95 }}
                  className="flex-1 py-3 rounded-xl font-bold"
                  style={{ backgroundColor: COLORS.green, color: COLORS.white }}
                >
                  {editingCoupon ? "Speichern" : "Erstellen"}
                </motion.button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Coupon List */}
      <div className="space-y-3">
        {coupons.length === 0 ? (
          <div className="text-center py-8" style={{ color: COLORS.textMuted }}>
            Noch keine Coupons erstellt
          </div>
        ) : (
          coupons.map((coupon) => (
            <motion.div
              key={coupon.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-2xl flex items-center gap-3"
              style={{ 
                backgroundColor: COLORS.white,
                opacity: coupon.active ? 1 : 0.6
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: COLORS.green + "20" }}
              >
                <Ticket size={24} weight="fill" style={{ color: COLORS.green }} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold" style={{ color: COLORS.text }}>{coupon.title}</h4>
                  {coupon.badge_type && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                      {coupon.badge_type}
                    </span>
                  )}
                  {coupon.is_welcome_gift && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600">
                      🎁 Welcome
                    </span>
                  )}
                </div>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  {coupon.value} · {coupon.valid_days} Tage · {coupon.redemption_count || 0}x eingelöst
                </p>
              </div>
              
              <div className="flex gap-2">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleToggleActive(coupon)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: coupon.active ? COLORS.green + "20" : COLORS.red + "20" }}
                >
                  {coupon.active ? (
                    <Check size={16} style={{ color: COLORS.green }} />
                  ) : (
                    <X size={16} style={{ color: COLORS.red }} />
                  )}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setEditingCoupon(coupon);
                    setFormData({
                      title: coupon.title,
                      description: coupon.description,
                      value: coupon.value,
                      valid_days: coupon.valid_days,
                      badge_type: coupon.badge_type || "",
                      is_welcome_gift: coupon.is_welcome_gift || false
                    });
                    setShowForm(true);
                  }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: COLORS.tan + "30" }}
                >
                  <Pencil size={16} style={{ color: COLORS.tan }} />
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleDelete(coupon.id)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: COLORS.red + "20" }}
                >
                  <Trash size={16} style={{ color: COLORS.red }} />
                </motion.button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SCANNER TAB
// ═══════════════════════════════════════════════════════════
const ScannerTab = ({ token }) => {
  const [mode, setMode] = useState("coupon"); // "coupon", "stamp", "membership", "balance", "freedrink"
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [membershipCustomer, setMembershipCustomer] = useState(null);
  const [redeemingMembership, setRedeemingMembership] = useState(false);
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [customerForFreeDrink, setCustomerForFreeDrink] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Handle QR Code scan from camera
  const handleQRScan = (detectedCodes) => {
    if (detectedCodes && detectedCodes.length > 0) {
      const scannedCode = detectedCodes[0].rawValue;
      if (scannedCode) {
        setCode(scannedCode.toUpperCase());
        setShowCamera(false);
        toast.success(`Code gescannt: ${scannedCode}`);
        // Auto-submit after scan
        setTimeout(() => {
          document.getElementById("scan-submit-btn")?.click();
        }, 300);
      }
    }
  };

  const handleScan = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    setMembershipCustomer(null);
    setBalanceInfo(null);
    setCustomerForFreeDrink(null);

    try {
      if (mode === "coupon") {
        const res = await axios.post(
          `${API}/api/backoffice/redeem-coupon`,
          { code: code.toUpperCase() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResult({ success: true, ...res.data });
        toast.success(res.data.message);
      } else if (mode === "stamp") {
        // Pre-Check: hat der Kunde eine aktive Membership? Dann nicht einfach
        // stempeln, sondern Info-Panel zeigen damit Mitarbeiter Membership
        // statt Stempel einlöst (verhindert Doppel-Vorteil).
        try {
          const infoRes = await axios.get(
            `${API}/api/staff/customer/${code.toUpperCase()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (infoRes.data?.membership) {
            setMembershipCustomer(infoRes.data);
            toast("⚠️ Aktive Flat — bitte prüfen statt Stempel zu geben", { icon: "⚠️" });
            setLoading(false);
            return;
          }
        } catch (e) {
          // Customer nicht gefunden — Stempel-Endpoint gibt eh aussagekräftigen Fehler
        }
        const res = await axios.post(
          `${API}/api/backoffice/stamp`,
          { member_code: code.toUpperCase() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResult({ success: res.data.success, ...res.data });
        if (res.data.success) {
          toast.success(res.data.message);
        } else {
          toast.error(res.data.message);
        }
      } else if (mode === "membership") {
        // Fetch customer info with membership
        const res = await axios.get(
          `${API}/api/staff/customer/${code.toUpperCase()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMembershipCustomer(res.data);
        if (!res.data.membership) {
          toast.info("Kunde hat kein aktives Membership");
        }
      } else if (mode === "freedrink") {
        // Fetch customer info for free drink redemption
        const res = await axios.get(
          `${API}/api/staff/customer/${code.toUpperCase()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const customer = res.data.customer;
        const freeDrinks = Math.floor((customer.stamps || 0) / 10);
        setCustomerForFreeDrink({
          ...customer,
          free_drinks: freeDrinks
        });
        if (freeDrinks === 0) {
          toast.info(`${customer.name} hat keine Freigetränke (${customer.stamps || 0} Stempel)`);
        }
      } else if (mode === "balance") {
        // Check if it's a gift card or member code
        const upperCode = code.toUpperCase();
        if (upperCode.startsWith("GC-")) {
          // Gift Card
          const res = await axios.get(
            `${API}/api/giftcard/${upperCode}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBalanceInfo({
            type: "gift_card",
            code: res.data.code,
            name: res.data.recipient_name || "Geschenkgutschein",
            balance: res.data.remaining,
            expires_at: res.data.expires_at
          });
        } else {
          // Member code - get user balance
          const res = await axios.get(
            `${API}/api/staff/customer/${upperCode}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBalanceInfo({
            type: "balance",
            code: upperCode,
            name: res.data.customer.name,
            balance: res.data.customer.balance || 0,
            pending_bonuses: res.data.customer.pending_bonuses || []
          });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler";
      setResult({ success: false, message: msg });
      toast.error(msg);
    }
    
    setLoading(false);
    if (mode !== "membership" && mode !== "balance" && mode !== "freedrink") setCode("");
  };

  const handleRedeemFreeDrink = async () => {
    if (!customerForFreeDrink || customerForFreeDrink.free_drinks === 0) return;
    setLoading(true);
    
    try {
      const res = await axios.post(
        `${API}/api/backoffice/redeem-free-drink`,
        { member_code: customerForFreeDrink.member_code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setResult({
        success: true,
        message: res.data.message,
        remaining_drinks: res.data.remaining_free_drinks
      });
      setCustomerForFreeDrink(null);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einlösen");
    }
    setLoading(false);
  };

  const handleRedeemBalance = async () => {
    if (!balanceInfo || !redeemAmount || parseFloat(redeemAmount) <= 0) {
      toast.error("Bitte gültigen Betrag eingeben");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/balance/redeem`,
        { 
          code: balanceInfo.code,
          amount: parseFloat(redeemAmount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setResult({
        success: true,
        message: res.data.message,
        amount_charged: res.data.amount_charged,
        remaining: res.data.remaining
      });
      setBalanceInfo(null);
      setCode("");
      setRedeemAmount("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einlösen");
    }
    setLoading(false);
  };

  const handleRedeemBonus = async (bonusId) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/bonus/redeem/${bonusId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setResult({
        success: true,
        message: res.data.message
      });
      // Refresh balance info
      handleScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einlösen");
    }
    setLoading(false);
  };

  const handleRedeemMembershipDrink = async (drinkName) => {
    if (!membershipCustomer) return;
    setRedeemingMembership(true);
    
    try {
      const res = await axios.post(
        `${API}/api/membership/redeem`,
        {
          user_id: membershipCustomer.customer.id,
          redemption_type: "drink",
          drink_name: drinkName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setResult({
        success: true,
        message: `${drinkName || "Getränk"} eingelöst`,
        remaining_drinks: res.data.remaining_drinks
      });
      setMembershipCustomer(null);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einlösen");
    }
    setRedeemingMembership(false);
  };

  const handleRedeemDiscount = async () => {
    if (!membershipCustomer) return;
    setRedeemingMembership(true);
    
    try {
      const res = await axios.post(
        `${API}/api/membership/redeem`,
        {
          user_id: membershipCustomer.customer.id,
          redemption_type: "discount"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${res.data.discount_percent}% Rabatt angewendet`);
      setResult({
        success: true,
        message: `${res.data.discount_percent}% Rabatt für ${membershipCustomer.customer.name}`,
        discount_percent: res.data.discount_percent
      });
      setMembershipCustomer(null);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einlösen");
    }
    setRedeemingMembership(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Scanner
      </h2>

      {/* Mode Toggle */}
      <div className="flex gap-1 p-1 rounded-xl flex-wrap" style={{ backgroundColor: COLORS.white }}>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => { setMode("coupon"); setMembershipCustomer(null); setResult(null); setBalanceInfo(null); setCustomerForFreeDrink(null); }}
          className="flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1 text-xs min-w-[60px]"
          style={{
            backgroundColor: mode === "coupon" ? COLORS.orange : "transparent",
            color: mode === "coupon" ? COLORS.white : COLORS.textMuted
          }}
        >
          <Ticket size={16} weight="fill" />
          Coupon
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => { setMode("stamp"); setMembershipCustomer(null); setResult(null); setBalanceInfo(null); setCustomerForFreeDrink(null); }}
          className="flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1 text-xs min-w-[60px]"
          style={{
            backgroundColor: mode === "stamp" ? COLORS.green : "transparent",
            color: mode === "stamp" ? COLORS.white : COLORS.textMuted
          }}
        >
          <Coffee size={16} weight="fill" />
          Stempel
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => { setMode("freedrink"); setMembershipCustomer(null); setResult(null); setBalanceInfo(null); setCustomerForFreeDrink(null); }}
          className="flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1 text-xs min-w-[60px]"
          style={{
            backgroundColor: mode === "freedrink" ? "#E74C3C" : "transparent",
            color: mode === "freedrink" ? COLORS.white : COLORS.textMuted
          }}
        >
          <Gift size={16} weight="fill" />
          Frei
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => { setMode("membership"); setMembershipCustomer(null); setResult(null); setBalanceInfo(null); setCustomerForFreeDrink(null); }}
          className="flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1 text-xs min-w-[60px]"
          style={{
            backgroundColor: mode === "membership" ? COLORS.tan : "transparent",
            color: mode === "membership" ? COLORS.white : COLORS.textMuted
          }}
        >
          <Star size={16} weight="fill" />
          Abo
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => { setMode("balance"); setMembershipCustomer(null); setResult(null); setBalanceInfo(null); setCustomerForFreeDrink(null); }}
          className="flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-1 text-xs min-w-[60px]"
          style={{
            backgroundColor: mode === "balance" ? "#7C3AED" : "transparent",
            color: mode === "balance" ? COLORS.white : COLORS.textMuted
          }}
        >
          <Wallet size={16} weight="fill" />
          Guthaben
        </motion.button>
      </div>

      {/* Input */}
      <div className="p-4 rounded-2xl space-y-4" style={{ backgroundColor: COLORS.white }}>
        {/* Camera Scanner */}
        <AnimatePresence>
          {showCamera && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="relative rounded-xl overflow-hidden mb-3"
              style={{ backgroundColor: "#000" }}
            >
              <div className="aspect-square max-h-64 relative">
                <Scanner
                  onScan={handleQRScan}
                  onError={(error) => {
                    setCameraError(error?.message || "Kamera-Fehler");
                    toast.error("Kamera konnte nicht gestartet werden");
                  }}
                  constraints={{ facingMode: "environment" }}
                  scanDelay={500}
                  styles={{
                    container: { width: "100%", height: "100%" },
                    video: { width: "100%", height: "100%", objectFit: "cover" }
                  }}
                />
                {/* Scan Frame Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    className="w-48 h-48 border-4 rounded-2xl"
                    style={{ borderColor: COLORS.green }}
                  />
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCamera(false)}
                className="absolute top-2 right-2 p-2 rounded-full"
                style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
              >
                <X size={20} className="text-white" />
              </motion.button>
              <p className="text-center text-white text-xs py-2">
                QR-Code in den Rahmen halten
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toggle: Camera vs Manual Input */}
        <div className="flex gap-2 mb-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCamera(true)}
            className="flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: showCamera ? COLORS.green : COLORS.bg,
              color: showCamera ? COLORS.white : COLORS.text
            }}
          >
            <Camera size={20} weight="fill" />
            Kamera
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCamera(false)}
            className="flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: !showCamera ? COLORS.green : COLORS.bg,
              color: !showCamera ? COLORS.white : COLORS.text
            }}
          >
            <Keyboard size={20} weight="fill" />
            Eintippen
          </motion.button>
        </div>

        {/* Manual Input */}
        {!showCamera && (
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={
                mode === "coupon" ? "Coupon-Code (z.B. EM-ABC123)" : 
                mode === "stamp" ? "Member-Code (z.B. FF-12345678)" :
                mode === "freedrink" ? "Member-Code für Freigetränk" :
                mode === "balance" ? "Member-Code oder GC-XXXXXXXX" :
                "Member-Code scannen"
              }
              className="flex-1 p-4 rounded-xl text-center text-lg font-mono font-bold uppercase"
              style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
              onKeyPress={(e) => e.key === "Enter" && handleScan()}
            />
          </div>
        )}

        {/* Scanned Code Display */}
        {code && (
          <div 
            className="p-3 rounded-xl flex items-center justify-between"
            style={{ backgroundColor: COLORS.green + "15" }}
          >
            <span className="font-mono font-bold" style={{ color: COLORS.green }}>{code}</span>
            <button onClick={() => setCode("")}>
              <X size={18} style={{ color: COLORS.textMuted }} />
            </button>
          </div>
        )}
        
        <motion.button
          id="scan-submit-btn"
          whileTap={{ scale: 0.95 }}
          onClick={handleScan}
          disabled={loading || !code.trim()}
          className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ 
            backgroundColor: mode === "coupon" ? COLORS.orange : mode === "stamp" ? COLORS.green : mode === "freedrink" ? "#E74C3C" : mode === "balance" ? "#7C3AED" : COLORS.tan,
            color: COLORS.white 
          }}
        >
          {loading ? (
            <span className="animate-spin">⏳</span>
          ) : (
            <>
              <Check size={24} weight="bold" />
              {mode === "coupon" ? "Coupon einlösen" : 
               mode === "stamp" ? "Stempel vergeben" : 
               mode === "freedrink" ? "Freigetränk prüfen" :
               mode === "balance" ? "Guthaben prüfen" : "Abo prüfen"}
            </>
          )}
        </motion.button>
      </div>

      {/* Balance Info Display */}
      <AnimatePresence>
        {balanceInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-5 rounded-2xl"
            style={{ backgroundColor: COLORS.white }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-black text-lg" style={{ color: COLORS.text }}>
                  {balanceInfo.name}
                </p>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  {balanceInfo.type === "gift_card" ? "Geschenkgutschein" : "Kunden-Guthaben"}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => { setBalanceInfo(null); setCode(""); setRedeemAmount(""); }}
                className="p-2 rounded-full"
                style={{ backgroundColor: COLORS.bg }}
              >
                <X size={20} style={{ color: COLORS.textMuted }} />
              </motion.button>
            </div>

            {/* Balance Amount */}
            <div 
              className="p-4 rounded-xl mb-4 text-center"
              style={{ backgroundColor: "#7C3AED15" }}
            >
              <p className="text-sm" style={{ color: "#7C3AED" }}>Verfügbares Guthaben</p>
              <p className="text-4xl font-black" style={{ color: "#7C3AED" }}>
                {balanceInfo.balance.toFixed(2)}€
              </p>
            </div>

            {/* Pending Bonuses */}
            {balanceInfo.pending_bonuses?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-bold mb-2" style={{ color: COLORS.text }}>🎁 Offene Boni:</p>
                {balanceInfo.pending_bonuses.map((bonus, i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-3 rounded-xl mb-2"
                    style={{ backgroundColor: COLORS.orange + "15" }}
                  >
                    <span className="font-bold" style={{ color: COLORS.orange }}>{bonus.text}</span>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRedeemBonus(bonus.id)}
                      className="px-3 py-1.5 rounded-lg font-bold text-sm text-white"
                      style={{ backgroundColor: COLORS.orange }}
                    >
                      Einlösen
                    </motion.button>
                  </div>
                ))}
              </div>
            )}

            {/* Redeem Amount Input */}
            <div className="space-y-3">
              <p className="text-sm font-bold" style={{ color: COLORS.text }}>Betrag abbuchen:</p>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={balanceInfo.balance}
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 p-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
                />
                <span className="flex items-center text-xl font-bold" style={{ color: COLORS.text }}>€</span>
              </div>
              
              {/* Quick amounts */}
              <div className="flex gap-2">
                {[5, 10, 15, 20].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setRedeemAmount(Math.min(amt, balanceInfo.balance).toString())}
                    className="flex-1 py-2 rounded-lg text-sm font-bold"
                    style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
                  >
                    {amt}€
                  </button>
                ))}
              </div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleRedeemBalance}
                disabled={loading || !redeemAmount || parseFloat(redeemAmount) <= 0 || parseFloat(redeemAmount) > balanceInfo.balance}
                className="w-full py-4 rounded-xl font-bold text-lg text-white disabled:opacity-50"
                style={{ backgroundColor: "#7C3AED" }}
              >
                {loading ? "..." : `${redeemAmount || "0"}€ abbuchen`}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Free Drink Customer Display */}
      <AnimatePresence>
        {customerForFreeDrink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-5 rounded-2xl"
            style={{ backgroundColor: COLORS.white }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-black text-lg" style={{ color: COLORS.text }}>
                  {customerForFreeDrink.name}
                </p>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  {customerForFreeDrink.member_code}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => { setCustomerForFreeDrink(null); setCode(""); }}
                className="p-2 rounded-full"
                style={{ backgroundColor: COLORS.bg }}
              >
                <X size={20} style={{ color: COLORS.textMuted }} />
              </motion.button>
            </div>

            {/* Stamps & Free Drinks Info */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div 
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: COLORS.green + "15" }}
              >
                <p className="text-3xl font-black" style={{ color: COLORS.green }}>
                  {customerForFreeDrink.stamps || 0}
                </p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Stempel gesamt</p>
              </div>
              <div 
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: customerForFreeDrink.free_drinks > 0 ? "#E74C3C15" : COLORS.bg }}
              >
                <p className="text-3xl font-black" style={{ color: customerForFreeDrink.free_drinks > 0 ? "#E74C3C" : COLORS.textMuted }}>
                  {customerForFreeDrink.free_drinks}
                </p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Freigetränke</p>
              </div>
            </div>

            {/* Redeem Button */}
            {customerForFreeDrink.free_drinks > 0 ? (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleRedeemFreeDrink}
                disabled={loading}
                className="w-full py-4 rounded-xl font-bold text-lg text-white flex items-center justify-center gap-2"
                style={{ backgroundColor: "#E74C3C" }}
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <>
                    <Gift size={24} weight="fill" />
                    Freigetränk einlösen
                  </>
                )}
              </motion.button>
            ) : (
              <div 
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: COLORS.bg }}
              >
                <p className="font-bold" style={{ color: COLORS.textMuted }}>
                  Keine Freigetränke verfügbar
                </p>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  Noch {10 - (customerForFreeDrink.stamps % 10)} Stempel bis zum nächsten
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Membership Customer Display */}
      <AnimatePresence>
        {membershipCustomer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-5 rounded-2xl"
            style={{ backgroundColor: COLORS.white }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-black text-lg" style={{ color: COLORS.text }}>
                  {membershipCustomer.customer.name}
                </p>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  {membershipCustomer.customer.member_code}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => { setMembershipCustomer(null); setCode(""); }}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: COLORS.bg }}
              >
                <X size={16} style={{ color: COLORS.textMuted }} />
              </motion.button>
            </div>

            {membershipCustomer.membership ? (
              <div className="space-y-4">
                {/* Plan Info */}
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: COLORS.green + "15" }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold" style={{ color: COLORS.green }}>
                        {membershipCustomer.membership.plan_name}
                      </p>
                      <p className="text-sm" style={{ color: COLORS.textMuted }}>
                        {membershipCustomer.membership.drink_type === "matcha" ? "Matcha Latte" :
                         membershipCustomer.membership.drink_type === "coffee" ? "Kaffee" : "Alle Getränke"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black" style={{ color: COLORS.green }}>
                        {membershipCustomer.membership.remaining_drinks === -1
                          ? "∞"
                          : membershipCustomer.membership.remaining_drinks}
                      </p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>
                        {membershipCustomer.membership.period === "week" ? "diese Woche" :
                         membershipCustomer.membership.period === "day" ? "heute" : "verfügbar"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* WARNING: Mitarbeiter-Hinweis */}
                <div
                  className="p-4 rounded-xl border-2"
                  style={{ backgroundColor: COLORS.orange + "12", borderColor: COLORS.orange }}
                >
                  <p className="font-black text-sm mb-1" style={{ color: COLORS.orange }}>
                    ⚠️ Achtung — bitte nicht falsch ausgeben
                  </p>
                  <p className="text-sm" style={{ color: COLORS.text }}>
                    Diesem Kunden gehört eine aktive <strong>{PLAN_DETAILS[membershipCustomer.membership.drink_type]?.label || membershipCustomer.membership.plan_name}</strong>-Flat.<br />
                    <strong>KEINE Stempel</strong> parallel geben — nur Membership einlösen!
                  </p>
                </div>

                {/* Was inklusive / nicht inklusive */}
                {PLAN_DETAILS[membershipCustomer.membership.drink_type] && (
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: COLORS.green + "10" }}
                    >
                      <p className="font-bold text-xs mb-2" style={{ color: COLORS.green }}>
                        ✅ INKLUSIVE
                      </p>
                      <ul className="text-xs space-y-1" style={{ color: COLORS.text }}>
                        {PLAN_DETAILS[membershipCustomer.membership.drink_type].includes.map((it, i) => (
                          <li key={i}>• {it}</li>
                        ))}
                      </ul>
                    </div>
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: COLORS.red + "10" }}
                    >
                      <p className="font-bold text-xs mb-2" style={{ color: COLORS.red }}>
                        ❌ NICHT INKLUSIVE
                      </p>
                      <ul className="text-xs space-y-1" style={{ color: COLORS.text }}>
                        {PLAN_DETAILS[membershipCustomer.membership.drink_type].excludes.map((it, i) => (
                          <li key={i}>• {it}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Frequenz / Reset Info */}
                {PLAN_DETAILS[membershipCustomer.membership.drink_type] && (
                  <p className="text-xs text-center" style={{ color: COLORS.textMuted }}>
                    {PLAN_DETAILS[membershipCustomer.membership.drink_type].frequency} · {PLAN_DETAILS[membershipCustomer.membership.drink_type].resetText}
                  </p>
                )}

                {/* Redeem Drink Button */}
                {(membershipCustomer.membership.remaining_drinks > 0 || membershipCustomer.membership.remaining_drinks === -1) && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleRedeemMembershipDrink(
                      membershipCustomer.membership.drink_type === "matcha" ? "Matcha Latte" :
                      membershipCustomer.membership.drink_type === "coffee" ? "Kaffee" : "Getränk"
                    )}
                    disabled={redeemingMembership}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: COLORS.green }}
                  >
                    {redeemingMembership ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <Coffee size={24} weight="fill" />
                        {membershipCustomer.membership.drink_type === "matcha" ? "Matcha einlösen" :
                         membershipCustomer.membership.drink_type === "coffee" ? "Kaffee einlösen" : "Getränk einlösen"}
                      </>
                    )}
                  </motion.button>
                )}

                {membershipCustomer.membership.remaining_drinks === 0 && (
                  <div className="p-4 rounded-xl text-center" style={{ backgroundColor: COLORS.orange + "15" }}>
                    <p className="font-bold" style={{ color: COLORS.orange }}>
                      Limit erreicht
                    </p>
                    <p className="text-sm" style={{ color: COLORS.textMuted }}>
                      {membershipCustomer.membership.period === "week" 
                        ? "Nächste Woche wieder verfügbar" 
                        : "Morgen wieder verfügbar"}
                    </p>
                  </div>
                )}

                {/* Discount Button if applicable */}
                {membershipCustomer.membership.discount_percent > 0 && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleRedeemDiscount}
                    disabled={redeemingMembership}
                    className="w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: COLORS.orange, color: COLORS.white }}
                  >
                    {membershipCustomer.membership.discount_percent}% Rabatt anwenden
                  </motion.button>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: COLORS.bg }}>
                <p className="font-bold" style={{ color: COLORS.textMuted }}>
                  Kein aktives Membership
                </p>
                <p className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                  Kunde kann ein Abo unter "Membership" abschließen
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result */}
      <AnimatePresence>
        {result && !membershipCustomer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-6 rounded-2xl text-center"
            style={{ 
              backgroundColor: result.success ? COLORS.green : COLORS.red,
              color: COLORS.white
            }}
          >
            <p className="text-4xl mb-2">{result.success ? "✅" : "❌"}</p>
            <p className="text-xl font-bold">{result.message}</p>
            
            {result.reward_earned && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="mt-4 p-4 rounded-xl"
                style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
              >
                <p className="text-3xl mb-2">
                  {result.reward_earned === "lunch" ? "🍽️" : "☕"}
                </p>
                <p className="font-bold">
                  {result.reward_earned === "lunch" 
                    ? "GRATIS MITTAGESSEN verdient!"
                    : "GRATIS GETRÄNK verdient!"
                  }
                </p>
              </motion.div>
            )}
            
            {result.total_stamps !== undefined && (
              <p className="mt-2 text-sm opacity-80">
                Stempel: {result.total_stamps}/{10} | Karten: {result.cards_completed || 0}
              </p>
            )}

            {result.remaining_drinks !== undefined && result.remaining_drinks !== null && (
              <p className="mt-2 text-sm opacity-80">
                {result.remaining_drinks === -1 
                  ? "Unbegrenzte Getränke" 
                  : `Noch ${result.remaining_drinks} Getränk(e) übrig`}
              </p>
            )}

            {result.discount_percent && (
              <p className="mt-2 text-sm opacity-80">
                Rabatt wurde vermerkt
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h4 className="font-bold mb-2" style={{ color: COLORS.text }}>
          {mode === "membership" ? "Membership-System" : "Stempelkarten-System"}
        </h4>
        {mode === "membership" ? (
          <ul className="text-sm space-y-1" style={{ color: COLORS.textMuted }}>
            <li>• <strong>Matcha Lover:</strong> 1 Matcha/Woche + 20% Rabatt</li>
            <li>• <strong>Coffee Pass:</strong> 1 Kaffee/Tag</li>
            <li>• <strong>VIP All-In:</strong> Unbegrenzte Basis-Getränke</li>
          </ul>
        ) : (
          <ul className="text-sm space-y-1" style={{ color: COLORS.textMuted }}>
            <li>• 10 Stempel = Gratis Getränk</li>
            <li>• 3x volle Karte = Gratis Mittagessen</li>
            <li>• 15 Min. Pause zwischen Stempeln</li>
          </ul>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// RESERVATIONS TAB
// ═══════════════════════════════════════════════════════════
const ReservationsTab = ({ token, location }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming"); // "upcoming", "today", "all"

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async () => {
    try {
      const res = await axios.get(`${API}/api/reservations/all?location=${location}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservations(res.data || []);
    } catch (err) {
      console.error("Error loading reservations:", err);
      toast.error("Fehler beim Laden der Reservierungen");
    }
    setLoading(false);
  };

  const handleStatusUpdate = async (reservationId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/reservations/${reservationId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status auf "${newStatus}" geändert`);
      loadReservations();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  // Filter reservations
  const today = new Date().toISOString().split('T')[0];
  const filteredReservations = reservations.filter(r => {
    if (filter === "today") return r.date === today;
    if (filter === "upcoming") return r.date >= today;
    return true; // "all"
  });

  // Sort by date and time
  const sortedReservations = [...filteredReservations].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.time.localeCompare(b.time);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return COLORS.green;
      case "cancelled": return COLORS.red;
      case "completed": return COLORS.textMuted;
      default: return COLORS.orange; // pending
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "confirmed": return "Bestätigt";
      case "cancelled": return "Storniert";
      case "completed": return "Abgeschlossen";
      default: return "Ausstehend";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('de-DE', options);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Tischreservierungen
      </h2>

      {/* Filter Buttons */}
      <div className="flex gap-2 p-1 rounded-xl" style={{ backgroundColor: COLORS.white }}>
        {[
          { id: "today", label: "Heute" },
          { id: "upcoming", label: "Kommende" },
          { id: "all", label: "Alle" }
        ].map((f) => (
          <motion.button
            key={f.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFilter(f.id)}
            className="flex-1 py-2.5 rounded-lg font-bold text-sm"
            style={{
              backgroundColor: filter === f.id ? COLORS.green : "transparent",
              color: filter === f.id ? COLORS.white : COLORS.textMuted
            }}
          >
            {f.label}
          </motion.button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 rounded-xl text-center" style={{ backgroundColor: COLORS.white }}>
          <p className="text-2xl font-black" style={{ color: COLORS.orange }}>
            {reservations.filter(r => r.status === "pending" && r.date >= today).length}
          </p>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>Ausstehend</p>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ backgroundColor: COLORS.white }}>
          <p className="text-2xl font-black" style={{ color: COLORS.green }}>
            {reservations.filter(r => r.status === "confirmed" && r.date >= today).length}
          </p>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>Bestätigt</p>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ backgroundColor: COLORS.white }}>
          <p className="text-2xl font-black" style={{ color: COLORS.text }}>
            {reservations.filter(r => r.date === today).length}
          </p>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>Heute</p>
        </div>
      </div>

      {/* Reservations List */}
      {loading ? (
        <div className="text-center py-8">
          <span className="animate-spin text-3xl">☕</span>
        </div>
      ) : sortedReservations.length === 0 ? (
        <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: COLORS.white }}>
          <CalendarBlank size={48} weight="light" style={{ color: COLORS.textMuted }} className="mx-auto mb-2" />
          <p className="font-bold" style={{ color: COLORS.textMuted }}>
            Keine Reservierungen
          </p>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            {filter === "today" ? "Heute keine Reservierungen" : "Noch keine Reservierungen vorhanden"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedReservations.map((reservation, i) => (
            <motion.div
              key={reservation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="p-4 rounded-2xl"
              style={{ backgroundColor: COLORS.white }}
            >
              {/* Header with Date/Time and Status */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex flex-col items-center justify-center"
                    style={{ backgroundColor: reservation.date === today ? COLORS.orange + "20" : COLORS.green + "15" }}
                  >
                    <CalendarBlank 
                      size={20} 
                      weight="fill" 
                      style={{ color: reservation.date === today ? COLORS.orange : COLORS.green }} 
                    />
                    <span 
                      className="text-xs font-bold"
                      style={{ color: reservation.date === today ? COLORS.orange : COLORS.green }}
                    >
                      {new Date(reservation.date).getDate()}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold" style={{ color: COLORS.text }}>
                      {formatDate(reservation.date)}
                    </p>
                    <p className="text-sm flex items-center gap-1" style={{ color: COLORS.textMuted }}>
                      <Clock size={14} />
                      {reservation.time} Uhr
                    </p>
                  </div>
                </div>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ 
                    backgroundColor: getStatusColor(reservation.status) + "20",
                    color: getStatusColor(reservation.status)
                  }}
                >
                  {getStatusLabel(reservation.status)}
                </span>
              </div>

              {/* Customer Info */}
              <div className="flex items-center gap-3 mb-3 p-3 rounded-xl" style={{ backgroundColor: COLORS.bg }}>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: COLORS.tan + "30" }}
                >
                  <User size={20} weight="fill" style={{ color: COLORS.tan }} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm" style={{ color: COLORS.text }}>
                    {reservation.name || "Gast"}
                  </p>
                  {reservation.phone && (
                    <p className="text-xs flex items-center gap-1" style={{ color: COLORS.textMuted }}>
                      <Phone size={12} />
                      {reservation.phone}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-lg font-black" style={{ color: COLORS.green }}>
                    {reservation.guests}
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Personen</p>
                </div>
              </div>

              {/* Notes */}
              {reservation.notes && (
                <p className="text-sm mb-3 p-2 rounded-lg" style={{ backgroundColor: COLORS.bg, color: COLORS.textMuted }}>
                  📝 {reservation.notes}
                </p>
              )}

              {/* Action Buttons */}
              {reservation.status === "pending" && (
                <div className="flex gap-2">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleStatusUpdate(reservation.id, "confirmed")}
                    className="flex-1 py-2 rounded-xl font-bold text-sm flex items-center justify-center gap-1"
                    style={{ backgroundColor: COLORS.green, color: COLORS.white }}
                  >
                    <Check size={16} weight="bold" />
                    Bestätigen
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleStatusUpdate(reservation.id, "cancelled")}
                    className="flex-1 py-2 rounded-xl font-bold text-sm flex items-center justify-center gap-1"
                    style={{ backgroundColor: COLORS.red + "20", color: COLORS.red }}
                  >
                    <X size={16} weight="bold" />
                    Stornieren
                  </motion.button>
                </div>
              )}

              {reservation.status === "confirmed" && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleStatusUpdate(reservation.id, "completed")}
                  className="w-full py-2 rounded-xl font-bold text-sm"
                  style={{ backgroundColor: COLORS.bg, color: COLORS.textMuted }}
                >
                  Als abgeschlossen markieren
                </motion.button>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Refresh Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={loadReservations}
        className="w-full py-3 rounded-xl font-bold text-sm"
        style={{ backgroundColor: COLORS.white, color: COLORS.textMuted }}
      >
        Aktualisieren
      </motion.button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MEMBERS TAB
// ═══════════════════════════════════════════════════════════
const MembersTab = ({ members, total }) => (
  <div className="space-y-4">
    <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
      Members ({total})
    </h2>
    
    <div className="space-y-2">
      {members.map((member) => (
        <motion.div
          key={member.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 rounded-2xl flex items-center gap-3"
          style={{ backgroundColor: COLORS.white }}
        >
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center font-bold"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            {member.name?.charAt(0).toUpperCase() || "?"}
          </div>
          <div className="flex-1">
            <h4 className="font-bold" style={{ color: COLORS.text }}>{member.name}</h4>
            <p className="text-xs font-mono" style={{ color: COLORS.textMuted }}>
              {member.member_code}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold" style={{ color: COLORS.green }}>
              {member.stamps || 0}/10 ☕
            </p>
            {member.membership?.active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                ⭐ {member.membership.plan_name}
              </span>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// REWARDS TAB
// ═══════════════════════════════════════════════════════════
const RewardsTab = ({ rewards, onClaim, token }) => {
  const handleClaim = async (rewardId) => {
    try {
      await axios.post(
        `${API}/api/backoffice/rewards/${rewardId}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Belohnung eingelöst!");
      onClaim();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Offene Belohnungen
      </h2>
      
      {rewards.length === 0 ? (
        <div className="text-center py-8" style={{ color: COLORS.textMuted }}>
          Keine offenen Belohnungen
        </div>
      ) : (
        <div className="space-y-3">
          {rewards.map((reward) => (
            <motion.div
              key={reward.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-2xl flex items-center gap-3"
              style={{ backgroundColor: COLORS.white }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                style={{ 
                  backgroundColor: reward.type === "lunch" ? COLORS.orange + "20" : COLORS.green + "20" 
                }}
              >
                {reward.type === "lunch" ? "🍽️" : "☕"}
              </div>
              <div className="flex-1">
                <h4 className="font-bold" style={{ color: COLORS.text }}>
                  {reward.type === "lunch" ? "Gratis Mittagessen" : "Gratis Getränk"}
                </h4>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  {reward.user_name} · {reward.member_code}
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleClaim(reward.id)}
                className="px-4 py-2 rounded-xl font-bold"
                style={{ backgroundColor: COLORS.green, color: COLORS.white }}
              >
                Einlösen
              </motion.button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// PUSH NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════
const PushNotificationsTab = ({ token, location }) => {
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    image_url: "",
    action_url: "",
    target: "all" // "all", "members", "non_members"
  });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/backoffice/push/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data || []);
    } catch (err) {
      console.error("Error loading push history:", err);
    }
    setLoadingHistory(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Titel und Nachricht sind erforderlich");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API}/api/backoffice/push/send`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Push-Nachricht wird versendet!");
      setFormData({ title: "", body: "", image_url: "", action_url: "", target: "all" });
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Push-Nachrichten
      </h2>

      {/* Compose Form */}
      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSend}
        className="p-5 rounded-2xl space-y-4"
        style={{ backgroundColor: COLORS.white }}
      >
        <h3 className="font-bold flex items-center gap-2" style={{ color: COLORS.text }}>
          <PaperPlaneTilt size={20} weight="fill" style={{ color: COLORS.green }} />
          Neue Nachricht erstellen
        </h3>

        {/* Title */}
        <div>
          <label className="text-sm font-bold mb-1 block" style={{ color: COLORS.textMuted }}>
            Titel *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="z.B. Neue Aktion!"
            className="w-full p-3 rounded-xl border-2"
            style={{ borderColor: COLORS.bg }}
            maxLength={50}
          />
          <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
            {formData.title.length}/50 Zeichen
          </p>
        </div>

        {/* Body */}
        <div>
          <label className="text-sm font-bold mb-1 block" style={{ color: COLORS.textMuted }}>
            Nachricht *
          </label>
          <textarea
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            placeholder="Beschreibe dein Angebot oder deine Nachricht..."
            className="w-full p-3 rounded-xl border-2 resize-none"
            style={{ borderColor: COLORS.bg }}
            rows={3}
            maxLength={200}
          />
          <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
            {formData.body.length}/200 Zeichen
          </p>
        </div>

        {/* Image URL */}
        <div>
          <label className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: COLORS.textMuted }}>
            <ImageIcon size={16} />
            Bild-URL (optional)
          </label>
          <input
            type="url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://beispiel.de/bild.jpg"
            className="w-full p-3 rounded-xl border-2"
            style={{ borderColor: COLORS.bg }}
          />
        </div>

        {/* Action URL */}
        <div>
          <label className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: COLORS.textMuted }}>
            <LinkIcon size={16} />
            Link beim Klick (optional)
          </label>
          <input
            type="url"
            value={formData.action_url}
            onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
            placeholder="https://frankysfarm.de/angebot"
            className="w-full p-3 rounded-xl border-2"
            style={{ borderColor: COLORS.bg }}
          />
        </div>

        {/* Target Audience */}
        <div>
          <label className="text-sm font-bold mb-2 block" style={{ color: COLORS.textMuted }}>
            Zielgruppe
          </label>
          <div className="flex gap-2">
            {[
              { id: "all", label: "Alle Kunden" },
              { id: "members", label: "Nur Members" },
              { id: "non_members", label: "Ohne Membership" }
            ].map((opt) => (
              <motion.button
                key={opt.id}
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={() => setFormData({ ...formData, target: opt.id })}
                className="flex-1 py-2 px-3 rounded-xl text-sm font-bold"
                style={{
                  backgroundColor: formData.target === opt.id ? COLORS.green : COLORS.bg,
                  color: formData.target === opt.id ? COLORS.white : COLORS.textMuted
                }}
              >
                {opt.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Preview */}
        {(formData.title || formData.body) && (
          <div 
            className="p-4 rounded-xl border-2 border-dashed"
            style={{ borderColor: COLORS.orange }}
          >
            <p className="text-xs font-bold mb-2" style={{ color: COLORS.orange }}>
            Vorschau
            </p>
            <div className="flex items-start gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                style={{ backgroundColor: COLORS.green }}
              >
                🌿
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate" style={{ color: COLORS.text }}>
                  {formData.title || "Titel..."}
                </p>
                <p className="text-xs line-clamp-2" style={{ color: COLORS.textMuted }}>
                  {formData.body || "Nachricht..."}
                </p>
              </div>
              {formData.image_url && (
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>
          </div>
        )}

        {/* Send Button */}
        <motion.button
          type="submit"
          whileTap={{ scale: 0.95 }}
          disabled={sending || !formData.title.trim() || !formData.body.trim()}
          className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: COLORS.green, color: COLORS.white }}
        >
          {sending ? (
            <>
              <span className="animate-spin">⏳</span>
              Wird gesendet...
            </>
          ) : (
            <>
              <PaperPlaneTilt size={24} weight="fill" />
              Jetzt senden an {location.charAt(0).toUpperCase() + location.slice(1)}
            </>
          )}
        </motion.button>

        <p className="text-xs text-center" style={{ color: COLORS.textMuted }}>
          Die Nachricht wird nur an Kunden in deiner Filiale ({location}) gesendet.
        </p>
      </motion.form>

      {/* Recent Notifications */}
      <div className="p-5 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: COLORS.text }}>
          Letzte Nachrichten
        </h3>

        {loadingHistory ? (
          <div className="text-center py-4">
            <span className="animate-spin text-2xl">☕</span>
          </div>
        ) : history.length === 0 ? (
          <p className="text-center py-4" style={{ color: COLORS.textMuted }}>
            Noch keine Nachrichten gesendet
          </p>
        ) : (
          <div className="space-y-3">
            {history.slice(0, 5).map((notif, i) => (
              <motion.div
                key={notif.id || i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="p-3 rounded-xl flex items-start gap-3"
                style={{ backgroundColor: COLORS.bg }}
              >
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: COLORS.green + "20" }}
                >
                  <Bell size={16} weight="fill" style={{ color: COLORS.green }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate" style={{ color: COLORS.text }}>
                    {notif.title}
                  </p>
                  <p className="text-xs truncate" style={{ color: COLORS.textMuted }}>
                    {notif.body}
                  </p>
                  <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
                    {notif.sent_count || 0} Empfänger • {new Date(notif.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.orange + "15" }}>
        <h4 className="font-bold mb-2" style={{ color: COLORS.orange }}>
          Hinweise
        </h4>
        <ul className="text-sm space-y-1" style={{ color: COLORS.textMuted }}>
          <li>• Nachrichten werden sofort an alle Kunden mit der App gesendet</li>
          <li>• Nutze Emojis für mehr Aufmerksamkeit</li>
          <li>• Bilder werden als Vorschau angezeigt</li>
          <li>• Max. 3 Push-Nachrichten pro Tag empfohlen</li>
        </ul>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// DELIVERY ZONES TAB
// ═══════════════════════════════════════════════════════════
const DeliveryZonesTab = ({ token }) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingZone, setEditingZone] = useState(null);
  const [newZone, setNewZone] = useState({ plz: "", name: "", delivery_fee: 2.50, min_order: 10.0, active: true });

  useEffect(() => {
    loadZones();
  }, []);

  const loadZones = async () => {
    try {
      const res = await axios.get(`${API}/api/delivery/zones`);
      setZones(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Liefergebiete");
    }
    setLoading(false);
  };

  const saveZone = async (zone) => {
    try {
      await axios.post(`${API}/api/delivery/zones`, zone, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Zone ${zone.plz} gespeichert`);
      loadZones();
      setEditingZone(null);
      setNewZone({ plz: "", name: "", delivery_fee: 2.50, min_order: 10.0, active: true });
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const toggleZoneActive = async (zone) => {
    await saveZone({ ...zone, active: !zone.active });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Liefergebiete verwalten
      </h2>

      {/* Add New Zone */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-3" style={{ color: COLORS.text }}>Neues Liefergebiet</h3>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={newZone.plz}
            onChange={(e) => setNewZone({ ...newZone, plz: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            placeholder="PLZ (z.B. 52062)"
            className="p-3 rounded-xl text-sm"
            style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
          />
          <input
            type="text"
            value={newZone.name}
            onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
            placeholder="Name (z.B. Aachen Mitte)"
            className="p-3 rounded-xl text-sm"
            style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
          />
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: COLORS.textMuted }}>Liefergebühr:</span>
            <input
              type="number"
              value={newZone.delivery_fee}
              onChange={(e) => setNewZone({ ...newZone, delivery_fee: parseFloat(e.target.value) || 0 })}
              step="0.5"
              className="p-2 rounded-lg text-sm w-20 text-center"
              style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
            />
            <span className="text-sm">€</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: COLORS.textMuted }}>Min. Bestellung:</span>
            <input
              type="number"
              value={newZone.min_order}
              onChange={(e) => setNewZone({ ...newZone, min_order: parseFloat(e.target.value) || 0 })}
              step="1"
              className="p-2 rounded-lg text-sm w-20 text-center"
              style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
            />
            <span className="text-sm">€</span>
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => newZone.plz.length === 5 && saveZone(newZone)}
          disabled={newZone.plz.length !== 5}
          className="mt-3 w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
          style={{ backgroundColor: newZone.plz.length === 5 ? COLORS.green : COLORS.textMuted }}
        >
          <Plus size={18} weight="bold" />
          Hinzufügen
        </motion.button>
      </div>

      {/* Zones List */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-center py-4" style={{ color: COLORS.textMuted }}>Laden...</p>
        ) : zones.length === 0 ? (
          <p className="text-center py-4" style={{ color: COLORS.textMuted }}>Keine Liefergebiete</p>
        ) : (
          zones.map((zone) => (
            <motion.div
              key={zone.plz}
              layout
              className="p-4 rounded-2xl flex items-center justify-between"
              style={{ 
                backgroundColor: COLORS.white,
                opacity: zone.active ? 1 : 0.5
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center font-black"
                  style={{ backgroundColor: zone.active ? COLORS.green + "15" : COLORS.bg, color: zone.active ? COLORS.green : COLORS.textMuted }}
                >
                  <MapPin size={20} weight="fill" />
                </div>
                <div>
                  <p className="font-bold" style={{ color: COLORS.text }}>{zone.plz}</p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>{zone.name || "Ohne Name"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-bold" style={{ color: COLORS.green }}>{zone.delivery_fee?.toFixed(2)}€</p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Min. {zone.min_order?.toFixed(0)}€</p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => toggleZoneActive(zone)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: zone.active ? COLORS.green : COLORS.red + "20" }}
                >
                  {zone.active ? (
                    <Check size={18} weight="bold" className="text-white" />
                  ) : (
                    <X size={18} weight="bold" style={{ color: COLORS.red }} />
                  )}
                </motion.button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SETTINGS TAB (Drucker, Benachrichtigungen etc.)
// ═══════════════════════════════════════════════════════════
const SettingsTab = ({ token, location }) => {
  const [settings, setSettings] = useState({
    auto_print: false,
    sound_enabled: true,
    firebase_connected: false,
    printer_name: ""
  });
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Bitte E-Mail-Adresse eingeben");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/api/email/test`, { email: testEmailAddress });
      toast.success("Test-E-Mail gesendet!");
    } catch (err) {
      toast.error("Fehler beim Senden: " + (err.response?.data?.detail || err.message));
    }
    setSendingEmail(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
        Einstellungen
      </h2>

      {/* Email Test */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: COLORS.text }}>
          <PaperPlaneTilt size={20} weight="fill" style={{ color: COLORS.green }} />
          E-Mail Versand testen
        </h3>
        <p className="text-sm mb-3" style={{ color: COLORS.textMuted }}>
          Sende eine Test-E-Mail um die Resend-Integration zu prüfen
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            placeholder="deine@email.de"
            className="flex-1 p-3 rounded-xl text-sm"
            style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
          />
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleTestEmail}
            disabled={sendingEmail}
            className="px-4 py-3 rounded-xl font-bold text-white"
            style={{ backgroundColor: COLORS.green }}
          >
            {sendingEmail ? "..." : "Senden"}
          </motion.button>
        </div>
      </div>

      {/* Drucker Einstellungen */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: COLORS.text }}>
          <CookingPot size={20} weight="fill" style={{ color: COLORS.orange }} />
          Bondrucker (Kitchen)
        </h3>
        <p className="text-sm mb-3" style={{ color: COLORS.textMuted }}>
          Automatischer Druck auf Star Micronics Bluetooth-Drucker
        </p>
        
        <div className="p-3 rounded-xl mb-3" style={{ backgroundColor: COLORS.bg }}>
          <p className="text-sm font-bold mb-2" style={{ color: COLORS.text }}>
            Drucker einrichten:
          </p>
          <ol className="text-xs space-y-1" style={{ color: COLORS.textMuted }}>
            <li>1. Star Micronics Drucker über Bluetooth koppeln</li>
            <li>2. Im Kitchen Dashboard "Bon drucken" klicken</li>
            <li>3. Drucker im Browser-Druckdialog auswählen</li>
            <li>4. "Als Standard speichern" aktivieren</li>
          </ol>
        </div>

        <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.orange + "15" }}>
          <p className="text-xs" style={{ color: COLORS.orange }}>
            <strong>Hinweis:</strong> Direkter Bluetooth-Zugriff vom Browser ist eingeschränkt. 
            Die Druckfunktion nutzt den nativen Browser-Druckdialog.
          </p>
        </div>
      </div>

      {/* Firebase Push */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: COLORS.text }}>
          <Bell size={20} weight="fill" style={{ color: COLORS.red }} />
          Push-Benachrichtigungen (Firebase)
        </h3>
        
        <div className="flex items-center justify-between p-3 rounded-xl mb-3" style={{ backgroundColor: COLORS.bg }}>
          <div>
            <p className="font-bold text-sm" style={{ color: COLORS.text }}>Status</p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>
              {settings.firebase_connected ? "Verbunden" : "Nicht verbunden (MOCKED)"}
            </p>
          </div>
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: settings.firebase_connected ? COLORS.green : COLORS.orange + "30" }}
          >
            {settings.firebase_connected ? (
              <Check size={16} weight="bold" className="text-white" />
            ) : (
              <X size={16} weight="bold" style={{ color: COLORS.orange }} />
            )}
          </div>
        </div>

        <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.red + "10" }}>
          <p className="text-xs" style={{ color: COLORS.red }}>
            <strong>Firebase Schlüssel benötigt:</strong> Um echte Push-Nachrichten zu senden, 
            wird ein Firebase Admin SDK Service Account Key (JSON) benötigt.
          </p>
        </div>
      </div>

      {/* Store Info */}
      <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
        <h3 className="font-bold mb-3" style={{ color: COLORS.text }}>
          Filiale: {location.charAt(0).toUpperCase() + location.slice(1)}
        </h3>
        <div className="space-y-2 text-sm" style={{ color: COLORS.textMuted }}>
          <p>📍 Pontstraße 137, 52062 Aachen</p>
          <p>📞 +49 241 123456</p>
          <p>📧 info@frankysfarm.de</p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN BACKOFFICE PAGE
// ═══════════════════════════════════════════════════════════
export default function BackofficePage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [activeTab, setActiveTab] = useState(isAdmin ? "dashboard" : "scanner");
  const [stats, setStats] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Wait for auth to initialize
    const checkAuth = setTimeout(() => {
      setAuthChecked(true);
      // Only navigate away if user is logged in but doesn't have permission
      if (user && !["staff", "admin"].includes(user.role)) {
        navigate("/");
      } else if (user && ["staff", "admin"].includes(user.role)) {
        loadData();
        // Set default tab based on role
        if (user.role === "staff") {
          setActiveTab("scanner");
        }
      }
    }, 500);
    
    return () => clearTimeout(checkAuth);
  }, [user]);

  useEffect(() => {
    if (user && ["staff", "admin"].includes(user.role) && authChecked) {
      loadData();
    }
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, couponsRes, membersRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/api/backoffice/stats`, { headers }),
        axios.get(`${API}/api/backoffice/coupons`, { headers }),
        axios.get(`${API}/api/backoffice/members`, { headers }),
        axios.get(`${API}/api/backoffice/rewards`, { headers })
      ]);
      
      setStats(statsRes.data);
      setCoupons(couponsRes.data);
      setMembers(membersRes.data.members);
      setMembersTotal(membersRes.data.total);
      setRewards(rewardsRes.data);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Fehler beim Laden");
    }
    setLoading(false);
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.bg }}>
        <span className="text-4xl animate-bounce">☕</span>
      </div>
    );
  }

  // Show login form for backoffice
  if (!user || !["staff", "admin"].includes(user.role)) {
    return <BackofficeLogin />;
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/landing")}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.white }}
          >
            <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
          </motion.button>
          <div>
            <h1 className="text-xl font-black" style={{ color: COLORS.text }}>
              Backoffice
            </h1>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              {user?.location?.charAt(0).toUpperCase() + user?.location?.slice(1)} · {user?.name}
            </p>
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => { logout(); navigate("/"); }}
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: COLORS.red + "20" }}
        >
          <SignOut size={22} style={{ color: COLORS.red }} />
        </motion.button>
      </header>

      <main className="px-4">
        {/* Tab Navigation */}
        <TabBar active={activeTab} onChange={setActiveTab} isAdmin={isAdmin} />

        {/* Tab Content */}
        {loading ? (
          <div className="text-center py-12">
            <span className="text-4xl animate-bounce">☕</span>
            <p className="mt-2" style={{ color: COLORS.textMuted }}>Laden...</p>
          </div>
        ) : (
          <>
            {activeTab === "dashboard" && (
              <DashboardTab stats={stats} location={user?.location || "aachen"} />
            )}
            {activeTab === "coupons" && (
              <CouponsTab coupons={coupons} onRefresh={loadData} token={token} />
            )}
            {activeTab === "scanner" && (
              <ScannerTab token={token} />
            )}
            {activeTab === "reservations" && (
              <ReservationsTab token={token} location={user?.location || "aachen"} />
            )}
            {activeTab === "members" && (
              <MembersTab members={members} total={membersTotal} />
            )}
            {activeTab === "rewards" && (
              <RewardsTab rewards={rewards} onClaim={loadData} token={token} />
            )}
            {activeTab === "push" && (
              <PushNotificationsTab token={token} location={user?.location || "aachen"} />
            )}
            {activeTab === "delivery" && (
              <DeliveryZonesTab token={token} />
            )}
            {activeTab === "newsletter" && (
              <NewsletterTab token={token} />
            )}
            {activeTab === "settings" && (
              <SettingsTab token={token} location={user?.location || "aachen"} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
